/** Thin fetch wrapper: JSON, CSRF header, readable errors. */
export class ApiError extends Error {
  constructor(public status: number, message: string, public data: Record<string, unknown> = {}) {
    super(message);
  }
  get details(): unknown {
    return this.data.details;
  }
}

const HEADERS = { 'X-Requested-With': 'calendar-studio' };

export async function api<T = unknown>(method: string, url: string, body?: unknown, opts: { signal?: AbortSignal } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: body === undefined ? HEADERS : { ...HEADERS, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'Cannot reach the server. Check the network connection and try again.');
  }
  const text = await res.text();
  let data: Record<string, unknown> = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { error: text.slice(0, 200) }; }
  if (!res.ok) {
    if (res.status === 401 && !url.includes('/auth/')) window.dispatchEvent(new Event('cs:unauthorized'));
    throw new ApiError(res.status, String(data.error ?? `Request failed (${res.status})`), data);
  }
  return data as T;
}

export const get = <T,>(url: string, signal?: AbortSignal) => api<T>('GET', url, undefined, { signal });
export const post = <T,>(url: string, body?: unknown) => api<T>('POST', url, body ?? {});
export const put = <T,>(url: string, body: unknown) => api<T>('PUT', url, body);
export const patch = <T,>(url: string, body: unknown) => api<T>('PATCH', url, body);
export const del = <T,>(url: string) => api<T>('DELETE', url);

/** Upload one file with progress (XHR gives upload progress events; fetch does not). */
export function uploadFile<T>(url: string, file: File | Blob, fields: Record<string, string> = {}, onProgress?: (f: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('X-Requested-With', 'calendar-studio');
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onload = () => {
      let data: Record<string, unknown> = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, String(data.error ?? `Upload failed (${xhr.status})`), data));
    };
    xhr.onerror = () => reject(new ApiError(0, 'Upload failed: network error.'));
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    fd.append('file', file, (file as File).name ?? 'upload');
    xhr.send(fd);
  });
}

export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
